<template>
  <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">

    <!-- Content -->
    <div class="py-4 md:py-8">
      <!-- Mobile Filter/Sort Buttons -->
      <div v-if="isMobile" class="px-2 mb-4">
        <FrontendCatalogSort v-model="activeSort" @update:sort="handleSortChange" button-variant="outline"
          :show-filter-button="true" @show-filters="isFilterOpen = true" />
      </div>

      <!-- Aktif Filtreler - Mobil -->
      <div v-if="isMobile" class="px-2 mb-4">
        <FrontendCatalogFiltersActiveFilters :filters="initialFilters || {}" :active-filters="activeFilters"
          @remove-brand="handleRemoveBrand" @remove-price="handleRemovePrice" @remove-attribute="handleRemoveAttribute"
          @reset="handleResetFilters" />
      </div>

      <div class="grid grid-cols-12 gap-4 md:gap-8">
        <!-- Filters Sidebar - Desktop -->
        <div v-if="!isMobile" class="col-span-3">
          <div :class="{ 'pointer-events-none opacity-50 animate-pulse': isHydrated && isLoading }">
            <FrontendCatalogFilters :filters="initialFilters || {}" :active-filters="activeFilters"
              :is-loading="isLoading" :category-selection-mode="categorySelectionMode"
              :is-category-selected="isCategorySelected" @update:filters="handleFilterUpdate"
              @select-category="handleCategorySelect" />
          </div>
        </div>

        <!-- Products Grid -->
        <div :class="[isMobile ? 'col-span-12 px-2' : 'col-span-9']">
          <!-- Sort Component ve Aktif Filtreler - Desktop -->
          <div v-if="!isMobile" class="mb-6">
            <div class="flex items-center justify-between gap-4">
              <div class="flex-1 min-w-0">
                <h1 v-if="route.query.q" class="text-xl "> "{{ route.query.q }}" <span class="font-normal">araması için
                    sonuçlar</span></h1>
                <FrontendCatalogFiltersActiveFilters :filters="initialFilters || {}" :active-filters="activeFilters"
                  @remove-brand="handleRemoveBrand" @remove-price="handleRemovePrice"
                  @remove-attribute="handleRemoveAttribute" @reset="handleResetFilters" />
              </div>
              <div class="shrink-0" :class="{ 'pointer-events-none opacity-50': isHydrated && isLoading }">
                <FrontendCatalogSort v-model="activeSort" @update:sort="handleSortChange" />
              </div>
            </div>
          </div>

          <!-- Products Grid -->
          <div v-if="catalogData?.data?.products?.data?.length > 0" :class="[
            'grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4',
            { 'pointer-events-none opacity-50 animate-pulse': isHydrated && isLoading }
          ]">
            <div v-for="product in catalogData?.data?.products?.data || []" :key="product.id" class="h-full">
              <FrontendProductCardSelector :product="product" class="h-full" @add-to-cart="handleAddToCart"
                @toggle-favorite="handleToggleFavorite" />
            </div>
          </div>

          <!-- Loading State -->
          <div v-else-if="isLoading" class="text-center py-12">
            <UCard :ui="{ rounded: 'rounded-none md:rounded-lg' }">
              <div class="flex flex-col items-center py-8">
                <div class="w-16 h-16 rounded-full bg-slate-200 animate-pulse mb-4"></div>
                <div class="h-6 w-48 bg-slate-200 animate-pulse mb-2"></div>
                <div class="h-4 w-64 bg-slate-200 animate-pulse"></div>
              </div>
            </UCard>
          </div>

          <!-- No Results -->
          <div v-else class="text-center py-12">
            <UCard :ui="{ rounded: 'rounded-none md:rounded-lg' }">
              <div class="flex flex-col items-center py-8">
                <UIcon name="i-heroicons-magnifying-glass" class="w-16 h-16 text-slate-300 mb-4" />
                <h3 class="text-xl font-medium mb-2 text-slate-700">Sonuç Bulunamadı</h3>
                <p class="text-sm text-slate-500 max-w-md mb-6">Arama kriterlerinize uygun ürün bulunamadı. Lütfen
                  farklı arama terimleri kullanmayı veya filtreleri değiştirmeyi deneyin.</p>

                <div
                  v-if="Object.keys(activeFilters.attributes).length > 0 || activeFilters.brands.length > 0 || activeFilters.price.min !== null || activeFilters.price.max !== null"
                  class="mt-2">
                  <UButton color="orange" variant="outline" @click="handleResetFilters" icon="i-heroicons-arrow-path"
                    class="mt-2">
                    Filtreleri Temizle
                  </UButton>
                </div>
              </div>
            </UCard>
          </div>
        </div>
      </div>
    </div>


    <!-- Mobile Filter Slideover -->
    <USlideover v-model="isFilterOpen">
      <FrontendCatalogFilters :filters="initialFilters || {}" :active-filters="activeFilters" :is-loading="isLoading"
        :category-selection-mode="categorySelectionMode" :is-category-selected="isCategorySelected"
        @update:filters="handleFilterUpdate" @select-category="handleCategorySelect" @close="isFilterOpen = false" />
    </USlideover>
  </div>
</template>


<script setup>

const route = useRoute()
const router = useRouter()
const { API_ENDPOINTS } = useApiEndpoints()
const { isMobile } = useDevice()

// Active filters state
const activeFilters = ref({
  brands: [],
  price: {
    min: null,
    max: null
  },
  attributes: {},
  vendor_id: null,
  selected_categories: []
})

// İlk yüklemede gelen filtreleri saklamak için
const initialFilters = ref({})

// Sort state
const activeSort = ref(null)

// Mobile filter state
const isFilterOpen = ref(false)

// Loading state
const isHydrated = ref(false)

// İlk sayfa yüklemesi kontrolü
const isFirstPageLoad = ref(true)

// Mağaza sayfasında olup olmadığımızı kontrol et
const isVendorPage = computed(() => route.path.includes('/magaza/'))

// Kategori seçim modunu belirle (checkbox/link)
const categorySelectionMode = computed(() => isVendorPage.value ? 'checkbox' : 'link')

// Kategori seçim işleyicisi
const handleCategorySelect = (categoryId) => {
  if (!isVendorPage.value) return

  const newFilters = { ...activeFilters.value }
  const index = newFilters.selected_categories.indexOf(categoryId)

  if (index === -1) {
    // Kategori seçili değilse ekle
    newFilters.selected_categories.push(categoryId)
  } else {
    // Kategori seçili ise kaldır
    newFilters.selected_categories.splice(index, 1)
  }

  handleFilterUpdate(newFilters)
}

// Kategorinin seçili olup olmadığını kontrol et
const isCategorySelected = (categoryId) => {
  return activeFilters.value.selected_categories.includes(categoryId)
}



// URL'den filtreleri al
const initializeFiltersFromRoute = () => {
  const query = route.query

  // Markaları al
  const brands = query.brands ? query.brands.split(',').map(Number) : []

  // Fiyat aralığını al
  const price = {
    min: query.price_min ? Number(query.price_min) : null,
    max: query.price_max ? Number(query.price_max) : null
  }

  // Mağaza ID'sini al
  const vendor_id = query.mid ? Number(query.mid) : null

  // Seçili kategorileri al
  const selected_categories = query.scats ? query.scats.split(',').map(Number) : []

  // Özellikleri al
  const attributes = {}
  Object.keys(query).forEach((key) => {
    if (key.startsWith('attr_')) {
      const attrId = key.replace('attr_', '')
      attributes[attrId] = query[key].split(',').map(Number)
    }
  })

  // Sıralamayı al
  activeSort.value = query.sort || null

  // Aktif filtreleri güncelle
  activeFilters.value = {
    brands,
    price,
    attributes,
    vendor_id,
    selected_categories
  }
}

// URL'yi filtrelerle güncelle
const updateRouteWithFilters = async (filters) => {
  try {
    const query = { ...route.query }

    // Markaları ekle
    if (filters.brands?.length > 0) {
      query.brands = filters.brands.join(',')
    } else {
      delete query.brands
    }

    // Fiyat aralığını ekle
    if (filters.price?.min !== null) {
      query.price_min = filters.price.min
    } else {
      delete query.price_min
    }
    if (filters.price?.max !== null) {
      query.price_max = filters.price.max
    } else {
      delete query.price_max
    }

    // Mağaza ID'sini ekle
    if (filters.vendor_id !== null) {
      query.mid = filters.vendor_id
    } else {
      delete query.mid
    }

    // Seçili kategorileri ekle
    if (filters.selected_categories?.length > 0) {
      query.scats = filters.selected_categories.join(',')
    } else {
      delete query.scats
    }

    // Özellikleri ekle
    Object.keys(query).forEach((key) => {
      if (key.startsWith('attr_')) {
        delete query[key]
      }
    })
    Object.entries(filters.attributes || {}).forEach(([attrId, values]) => {
      if (values?.length > 0) {
        query[`attr_${attrId}`] = values.join(',')
      }
    })

    // Sıralamayı ekle
    if (activeSort.value) {
      query.sort = activeSort.value
    } else {
      delete query.sort
    }

    // Route'u güncelle (veri yenilemeyi route watcher yapacak)
    await router.push({ query })
  } finally {
    // no-op: loading useAsyncData pending tarafından yönetilir
  }
}

// URL'yi parse et
const parseRoute = () => {
  const rootParams = {}
  const filterParams = { ...route.query }

  // Kategori sayfası
  if (route.path.includes('/katalog/')) {
    const categoryMatch = route.path.match(/.*-c(\d+)$/)
    if (categoryMatch) {
      const categoryId = categoryMatch[1]
      const fullPath = route.path.split('-c')[0].split('/katalog/')[1]
      rootParams.slug = fullPath
      rootParams.category_id = categoryId
    }
  }

  // Mağaza sayfası
  if (route.path.includes('/magaza/')) {
    rootParams.magaza_slug = route.params.slug
  }

  // Arama sayfası
  if (route.path.includes('/arama')) {
    rootParams.search_query = route.query.q
    delete filterParams.q // q parametresini filterParams'dan çıkar
  }

  // Mağaza ID'sini ekle
  if (filterParams.mid) {
    rootParams.vendor_id = Number(filterParams.mid)
    delete filterParams.mid // mid parametresini filterParams'dan çıkar
  }

  // Seçili kategorileri ekle
  if (filterParams.scats) {
    filterParams.selected_categories = filterParams.scats
    delete filterParams.scats // scats parametresini kaldır
  }

  return {
    ...rootParams,
    filter_params: Object.keys(filterParams).length > 0 ? filterParams : undefined
  }
}

// Katalog verilerini getir
const { data: catalogData, pending: catalogPending, refresh: refreshCatalog } = await useAsyncData(
  `catalog-${route.fullPath}`,
  async () => {
    try {
      const params = parseRoute()
      const data = await useBaseOFetchWithAuth(API_ENDPOINTS.public.catalog.get, {
        query: {
          ...params,
          is_initial_load: isFirstPageLoad.value
        }
      })
      console.log(data)
      return data
    } catch (error) {
      console.error('Katalog verileri alınırken hata oluştu:', error)
      return { data: { products: { data: [] }, filters: {} } }
    }
  }
)

// Loading durumu: useAsyncData pending bayrağına bağlanır
const isLoading = computed(() => catalogPending.value)

// Filtreleri güncelle ve sakla
watch(catalogData, (newData) => {
  if (newData?.data?.filters) {
    initialFilters.value = newData.data.filters
    isFirstPageLoad.value = false
  }
}, { immediate: true })

// Event Handlers
const handleAddToCart = (product) => {
  console.log('Sepete eklendi:', product)
}

const handleToggleFavorite = (product) => {
  console.log('Favorilere eklendi:', product)
}

const handleFilterUpdate = async (newFilters) => {
  activeFilters.value = newFilters
  await updateRouteWithFilters(newFilters)
}

const handleSortChange = async (newSort) => {
  activeSort.value = newSort
  await updateRouteWithFilters(activeFilters.value)
}

const handleRemoveBrand = (brandId) => {
  const newFilters = { ...activeFilters.value }
  newFilters.brands = newFilters.brands.filter(id => id !== brandId)
  handleFilterUpdate(newFilters)
}

const handleRemovePrice = () => {
  const newFilters = { ...activeFilters.value }
  newFilters.price = { min: null, max: null }
  handleFilterUpdate(newFilters)
}

const handleRemoveAttribute = ({ attributeId, termId }) => {
  const newFilters = { ...activeFilters.value }
  const terms = newFilters.attributes[attributeId]
  if (terms) {
    const newTerms = terms.filter(id => id !== termId)
    if (newTerms.length === 0) {
      delete newFilters.attributes[attributeId]
    } else {
      newFilters.attributes[attributeId] = newTerms
    }
  }
  handleFilterUpdate(newFilters)
}

const handleResetFilters = () => {
  handleFilterUpdate({
    brands: [],
    price: {
      min: null,
      max: null
    },
    attributes: {},
    vendor_id: null,
    selected_categories: []
  })
}

// Sayfa yüklendiğinde filtreleri URL'den al
onMounted(() => {
  initializeFiltersFromRoute()
  isHydrated.value = true
})

// Route değişikliklerini izle
watch(
  [
    () => route.fullPath,
    () => route.query
  ],
  async ([newPath, newQuery], [oldPath, oldQuery]) => {
    try {
      // Sayfa değiştiğinde (path değişimi)
      if (newPath !== oldPath) {
        isFirstPageLoad.value = true
      }

      // Query veya path değişiminde filtreleri güncelle
      initializeFiltersFromRoute()
      await refreshCatalog()
    } catch (error) {
      console.error('Route değişiminde hata:', error)
    } finally {
      // no-op: loading useAsyncData pending tarafından yönetilir
    }
  },
  { deep: true }
)
</script>
